import { create } from 'zustand';

import { DEFAULT_PAGE_SIZE } from '@/shared/lib/constants';

import type { DagDashboardResponse } from '@/entities/dag';

import {
  DAG_FAVORITE_FILTER_VALUES,
  DAG_MATURITY_FILTER_VALUES,
  DAG_RUN_STATUS_FILTER_VALUES,
  type DagDashboardSortField,
  type DagDashboardSortOrder,
  type DagFavoriteFilterValue,
  type DagListFilterState,
  type DagMaturityFilterValue,
  type DagRunStatusFilterValue,
} from './types';

export const DAG_LIST_FRONTEND_LIMIT = 1000;

export const DEFAULT_DAG_LIST_FILTERS: DagListFilterState = {
  searchQuery: '',
  selectedRunStatus: [DAG_RUN_STATUS_FILTER_VALUES.ALL],
  selectedFavorite: [DAG_FAVORITE_FILTER_VALUES.ALL],
  selectedMaturity: [DAG_MATURITY_FILTER_VALUES.ALL],
  selectedTags: [],
};

type DagListDashboardState = {
  sourceItems: DagDashboardResponse[];
  isLoading: boolean;
  error: Error | null;
  preserveOnUnmount: boolean;
  filters: DagListFilterState;
  selectedVersionByDagUuid: Record<string, number | undefined>;
  sortBy?: DagDashboardSortField;
  sortOrder?: DagDashboardSortOrder;
  currentPage: number;
  pageSize: number;
};

type DagListDashboardActions = {
  setSourceItems: (items: DagDashboardResponse[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  markPreserveOnUnmount: () => void;
  clearPreserveOnUnmount: () => void;
  setSearchQuery: (searchQuery: string) => void;
  setSelectedRunStatus: (selectedRunStatus: DagRunStatusFilterValue[]) => void;
  setSelectedFavorite: (selectedFavorite: DagFavoriteFilterValue[]) => void;
  setSelectedMaturity: (selectedMaturity: DagMaturityFilterValue[]) => void;
  setSelectedTags: (selectedTags: string[]) => void;
  resetFilters: () => void;
  setSelectedVersion: (dagUuid: string, version: number | undefined) => void;
  setSort: (sortBy?: DagDashboardSortField, sortOrder?: DagDashboardSortOrder) => void;
  setPage: (currentPage: number, pageSize?: number) => void;
  resetPage: () => void;
  resetState: () => void;
};

type DagListDashboardStore = DagListDashboardState & DagListDashboardActions;

const INITIAL_STATE: DagListDashboardState = {
  sourceItems: [],
  isLoading: true,
  error: null,
  preserveOnUnmount: false,
  filters: DEFAULT_DAG_LIST_FILTERS,
  selectedVersionByDagUuid: {},
  sortBy: undefined,
  sortOrder: undefined,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const useDagListDashboardStore = create<DagListDashboardStore>((set) => ({
  ...INITIAL_STATE,
  setSourceItems: (sourceItems) => set({ sourceItems }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  markPreserveOnUnmount: () => set({ preserveOnUnmount: true }),
  clearPreserveOnUnmount: () => set({ preserveOnUnmount: false }),
  setSearchQuery: (searchQuery) =>
    set((state) => ({
      filters: { ...state.filters, searchQuery },
      currentPage: 1,
    })),
  setSelectedRunStatus: (selectedRunStatus) =>
    set((state) => ({
      filters: {
        ...state.filters,
        selectedRunStatus: selectedRunStatus.length === 0 ? [DAG_RUN_STATUS_FILTER_VALUES.ALL] : selectedRunStatus,
      },
      currentPage: 1,
    })),
  setSelectedFavorite: (selectedFavorite) =>
    set((state) => ({
      filters: {
        ...state.filters,
        selectedFavorite: selectedFavorite.length === 0 ? [DAG_FAVORITE_FILTER_VALUES.ALL] : selectedFavorite,
      },
      currentPage: 1,
    })),
  setSelectedMaturity: (selectedMaturity) =>
    set((state) => ({
      filters: {
        ...state.filters,
        selectedMaturity: selectedMaturity.length === 0 ? [DAG_MATURITY_FILTER_VALUES.ALL] : selectedMaturity,
      },
      currentPage: 1,
    })),
  setSelectedTags: (selectedTags) =>
    set((state) => ({
      filters: { ...state.filters, selectedTags },
      currentPage: 1,
    })),
  resetFilters: () =>
    set((state) => ({
      filters: DEFAULT_DAG_LIST_FILTERS,
      currentPage: 1,
      selectedVersionByDagUuid: state.selectedVersionByDagUuid,
    })),
  setSelectedVersion: (dagUuid, version) =>
    set((state) => ({
      selectedVersionByDagUuid: {
        ...state.selectedVersionByDagUuid,
        [dagUuid]: version,
      },
    })),
  setSort: (sortBy, sortOrder) =>
    set((state) => {
      if (state.sortBy === sortBy && state.sortOrder === sortOrder) {
        return state;
      }

      return {
        sortBy,
        sortOrder,
        currentPage: 1,
      };
    }),
  setPage: (currentPage, pageSize) =>
    set((state) => ({
      currentPage,
      pageSize: pageSize ?? state.pageSize,
    })),
  resetPage: () => set({ currentPage: 1 }),
  resetState: () => set(INITIAL_STATE),
}));
